import React from 'react'
import { Card, Typography } from 'antd'
import { formatPhoneNumber } from '@serviceflow/shared'
import { getPublicInvoicePayContext } from '../../utils/publicInvoicePayContext'

const { Text } = Typography

type AddressParts = { street?: string; city?: string; state?: string; zipCode?: string }

function parseAddress(address: unknown): AddressParts | null {
  if (!address) return null
  if (typeof address === 'object' && address !== null) {
    return address as AddressParts
  }
  if (typeof address === 'string') {
    if (address.trim().startsWith('{')) {
      try {
        return JSON.parse(address) as AddressParts
      } catch {
        return { street: address }
      }
    }
    return { street: address }
  }
  return null
}

function formatBusinessAddress(business: any): AddressParts {
  const parsed = parseAddress(business?.address)
  if (parsed && (parsed.street || parsed.city)) {
    return parsed
  }
  return {
    street:
      typeof business?.address === 'string' && !business.address.startsWith('{')
        ? business.address
        : undefined,
    city: business?.city,
    state: business?.state,
    zipCode: business?.zipCode,
  }
}

function money(amount: unknown): string {
  const n = Number(amount)
  if (!Number.isFinite(n)) return '$0.00'
  return `$${n.toFixed(2)}`
}

function isSectionRow(item: { type?: string; itemType?: string }): boolean {
  return (item?.type || item?.itemType) === 'section'
}

function TotalsRow({
  label,
  value,
  emphasize,
}: {
  label: string
  value: string
  emphasize?: boolean
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        padding: emphasize ? '10px 0 0' : '4px 0',
        marginTop: emphasize ? 6 : 0,
        borderTop: emphasize ? '1px solid #e5e7eb' : undefined,
        fontWeight: emphasize ? 700 : 400,
        fontSize: emphasize ? 16 : 13,
        color: emphasize ? '#111827' : '#4b5563',
      }}
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  )
}

export const PublicPayInvoiceSummary: React.FC<{
  invoice: any
  business: any
}> = ({ invoice, business }) => {
  const { payAmount, isDepositInvoice, totalAmount, paidAmount } = getPublicInvoicePayContext(invoice)
  const addr = formatBusinessAddress(business)
  const settings =
    typeof business?.settings === 'string'
      ? (() => {
          try {
            return JSON.parse(business.settings || '{}')
          } catch {
            return {}
          }
        })()
      : business?.settings || {}
  const phoneFormat = settings.phoneFormat || '(XXX) XXX-XXXX'
  const cityLine = [addr.city, addr.state].filter(Boolean).join(', ')
  const items: any[] = Array.isArray(invoice?.items) ? invoice.items : []
  const subtotal = Number(invoice?.subtotal) || 0
  const discountAmount = Number(invoice?.discountAmount) || 0
  const taxAmount = Number(invoice?.taxAmount) || 0
  const title = typeof invoice?.title === 'string' ? invoice.title.trim() : ''
  const description = typeof invoice?.description === 'string' ? invoice.description.trim() : ''

  return (
    <Card
      style={{ marginBottom: 16, borderRadius: 12 }}
      styles={{ body: { padding: 16 } }}
    >
      <Text type="secondary" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.08, textTransform: 'uppercase' }}>
        You are paying
      </Text>
      {business?.logo ? (
        <img
          src={business.logo}
          alt={business.name || 'Company logo'}
          style={{ display: 'block', maxHeight: 48, maxWidth: '100%', objectFit: 'contain', marginTop: 10 }}
        />
      ) : null}
      <div style={{ fontSize: 18, fontWeight: 700, color: '#111827', lineHeight: 1.3, marginTop: 8, overflowWrap: 'anywhere' }}>
        {business?.name || 'Company'}
      </div>
      <div style={{ color: '#6b7280', fontSize: 13, marginTop: 6, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
        {addr.street ? <div>{addr.street}</div> : null}
        {(cityLine || addr.zipCode) ? (
          <div>
            {cityLine}
            {addr.zipCode ? ` ${addr.zipCode}` : ''}
          </div>
        ) : null}
        {business?.phone ? (
          <div style={{ whiteSpace: 'nowrap' }}>{formatPhoneNumber(business.phone, phoneFormat)}</div>
        ) : null}
        {business?.email ? <div>{business.email}</div> : null}
      </div>

      <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f0f0f0' }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#111827', lineHeight: 1.35, overflowWrap: 'anywhere' }}>
          Invoice #{invoice?.invoiceNumber}
          {title ? ` · ${title}` : ''}
        </div>
        {description ? (
          <div style={{ color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.45, overflowWrap: 'anywhere' }}>{description}</div>
        ) : null}

        {items.length > 0 ? (
          <div style={{ marginTop: 12 }}>
            {items.map((item, index) => {
              const key = item.id || `line-${index}`
              if (isSectionRow(item)) {
                return (
                  <div
                    key={key}
                    style={{
                      fontWeight: 700,
                      fontSize: 12,
                      color: '#374151',
                      background: '#f9fafb',
                      padding: '6px 8px',
                      margin: '6px 0',
                      borderRadius: 6,
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {item.name}
                  </div>
                )
              }
              const qty = Number(item.quantity) || 0
              const unit = Number(item.unitPrice) || 0
              const lineTotal =
                item.totalPrice != null && item.totalPrice !== ''
                  ? Number(item.totalPrice)
                  : qty * unit
              return (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '10px 0',
                    borderBottom: '1px solid #f3f4f6',
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: '#111827', overflowWrap: 'anywhere' }}>{item.name}</div>
                    {item.description ? (
                      <div style={{ color: '#6b7280', fontSize: 12, marginTop: 4, lineHeight: 1.45, overflowWrap: 'anywhere' }}>{item.description}</div>
                    ) : null}
                    <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 4 }}>
                      {qty} × {money(unit)}
                    </div>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#111827', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {money(lineTotal)}
                  </div>
                </div>
              )
            })}
          </div>
        ) : null}

        <div style={{ marginTop: 10 }}>
          {subtotal > 0 ? <TotalsRow label="Subtotal" value={money(subtotal)} /> : null}
          {discountAmount > 0 ? <TotalsRow label="Discount" value={`−${money(discountAmount)}`} /> : null}
          {taxAmount > 0 ? <TotalsRow label="Tax" value={money(taxAmount)} /> : null}
          <TotalsRow label="Invoice total" value={money(totalAmount)} />
          {paidAmount > 0 ? <TotalsRow label="Already paid" value={`−${money(paidAmount)}`} /> : null}
          <TotalsRow
            label={isDepositInvoice ? 'Deposit due' : 'Amount due'}
            value={money(payAmount)}
            emphasize
          />
        </div>
      </div>
    </Card>
  )
}

export default PublicPayInvoiceSummary
